import React, { useEffect, useState } from 'react';
import { Button } from 'antd';
import type { Program } from '../../../interfaces/CamProgramming.interface';

/** `12:04`, or `1:12:04` once an hour has passed. */
function formatElapsed(totalSeconds: number): string {
	const seconds = totalSeconds % 60;
	const minutes = Math.floor(totalSeconds / 60) % 60;
	const hours = Math.floor(totalSeconds / 3600);
	const padded = [minutes, seconds].map((part) => String(part).padStart(2, '0'));
	return hours ? [hours, ...padded].join(':') : padded.join(':');
}

function useElapsedSeconds(startedAt: string | null): number {
	const [now, setNow] = useState(() => Date.now());

	useEffect(() => {
		if (!startedAt) return;
		const timer = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(timer);
	}, [startedAt]);

	if (!startedAt) return 0;
	return Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000));
}

interface ProgrammingClockProps {
	program: Program;
	onEnd: () => void;
	isEnding: boolean;
}

/**
 * How long the programming session has been running, and the way to stop it. A
 * finished session shows what it took instead.
 */
const ProgrammingClock: React.FC<ProgrammingClockProps> = ({
	program,
	onEnd,
	isEnding,
}) => {
	const startedAt = program.programming_started_at ?? program.created_time;
	const elapsed = useElapsedSeconds(
		program.programming_completed_at ? null : startedAt
	);

	if (program.programming_completed_at) {
		const minutes = program.programming_duration_minutes;
		return (
			<div className="embed-clock embed-clock--done">
				<span className="embed-clock__label">
					Programming ended{minutes ? ` after ${minutes} min` : ''}
				</span>
			</div>
		);
	}

	return (
		<div className="embed-clock">
			<span className="embed-clock__time" role="timer" aria-live="off">
				{formatElapsed(elapsed)}
			</span>
			<Button size="small" danger onClick={onEnd} loading={isEnding}>
				End Programming
			</Button>
		</div>
	);
};

export default ProgrammingClock;
